/*
 * Copyright (c) 2021 CalAmp Corp.  All Rights Reserved
 */
import produce from 'immer';
import isundefined from 'lodash.isundefined';
import {localPersistence} from '@ion/core/src/capabilities/storage';
import {translate} from '@ion/core/src/locale/locale';
import {
  getRoute,
  getRouteByFeature,
  isRoute,
  Route
} from '@/routes/RouteDefinitions';
import Features, {
  DASHBOARD_PAGE
} from '@/security/Features';
import {
  APPLICATION_INITIALIZATION_COMPLETE
} from '@/store/actions/initialization';
import {
  PAGE_SECURITY_INITIALIZED,
  SET_PAGE,
  SET_SIDE_SHEET_STATUS,
  TOGGLE_SECONDARY_NAV
} from '@/store/actions/nav';
import {ApplicationKeys} from '@/types/application';
import {Page} from '@/types/pages';

/**
 * @authorizedPages
 *
 * Provides a utility which traverses all page definitions are returns only the
 * pages to which the user is authorized access.
 */
export const authorizedPages = (pages: Page[]): Page[] => {
  return pages.filter(({pages, isAuthorized}: Page) => {
    if (!pages) {
      return isAuthorized;
    }
    return authorizedPages(pages as Page[]);
  });
};

/**
 * Defines a mapping of iOn Feature ids to their corresponding translation
 * entry keys.
 */
const pageFeatureIdMap = {
  [DASHBOARD_PAGE] : 'dashboard'
};

/**
 * @private
 *
 * Provides a utility which updates all authorized page names and labels with
 * the corresponding translation for the page's feature id.
 */
const translateAuthorizedPages = (pages: Page[]): Page[] => {
  return pages.map((page: Page) => {
    page.name = page.label = translate(`nav:side.${pageFeatureIdMap[page.id]}`);
    return page;
  });
};

/**
 * Provides a utility which returns the navigation item index for the given
 * route.
 */
const getIndex = (pages: Page[], page: string): number => {
  let index: number;
  pages.find(({pages: subpages, route}: Page, i) => {
    const match    = getRoute(route) === getRoute(page as Route);
    const hasChild = subpages && !isundefined(getIndex(subpages as Page[], page));
    if (match || hasChild) {
      index = i;
      return true;
    }
  });
  return index;
};

/**
 * @private
 */
const getFallbackPageId = (pages: Page[]): string => {
  return DASHBOARD_PAGE;
};

/**
 * @private
 */
const getDefaultPage = (route: any, fromLogin: boolean, pages: Page[]): Route => {
  const defaultPageId = DASHBOARD_PAGE;
  const defaultPage   = getRouteByFeature(
    Features.hasFeature(defaultPageId) ? defaultPageId : getFallbackPageId(pages)
  );
  if (!fromLogin) {
    return route?.pathname || localPersistence(ApplicationKeys.BMWPortalPage, 'route') || defaultPage;
  }
  return defaultPage;
};

// defines the shape of this reducers state ...
export type NavState = {
  pages: Page[],
  defaultPage: Route
  currentPage: string;
  previousPage: string;
  selectedIndex: number;
  isInternalRoute: boolean;
  isUserProfileView: boolean;
  pageSecurityInitialized: boolean;
  secondaryNav: {
    pages: Page[];
    show: boolean;
  }
  sideSheet: {
    activeSideSheet: null | string;
    desktopSideSheetOpen: boolean;
    mobileSideSheetOpen: boolean;
  }
};

// defines the initial state of this reducer ...
export const initialState: NavState = {
  pages                   : [],
  defaultPage             : Route.DASHBOARD,
  currentPage             : null,
  previousPage            : null,
  selectedIndex           : NaN,
  isInternalRoute         : false,
  isUserProfileView       : false,
  pageSecurityInitialized : false,
  secondaryNav            : {
    pages : [],
    show  : false
  },
  sideSheet : {
    activeSideSheet      : null,
    desktopSideSheetOpen : false,
    mobileSideSheetOpen  : false
  }
};

const DASHBOARD = {
  id           : DASHBOARD_PAGE,
  route        : Route.DASHBOARD,
  isActive     : true,
  isAuthorized : true,
  isPreview    : true
};

/**
 * Defines all CTC Admin application pages definitions. Upon post initialization
 * (after the user has authenticated), the pages are passed through the Features
 * API which filters them based on those to which the User is authorized to view
 * based on their given Role.
 *
 * In addition, when initially referenced, the isPreview value fro specific pages
 * will be set based on the value of the "featureFlagsEnabled" entry from the
 * applications environment.json document. This allow for externally enabling
 * and disabling of flagged features.
 */
export const pages = (): Page[] => {
  return [
    DASHBOARD
  ];
};

// defines the reducers ...
const reducers = {
  [SET_PAGE] : (draft: NavState, {page}) => {
    draft.selectedIndex   = getIndex(draft.pages, getRoute(page));
    draft.previousPage    = draft.currentPage;
    draft.currentPage     = getRoute(page);
    draft.isInternalRoute = true;
  },
  [APPLICATION_INITIALIZATION_COMPLETE] : (draft: NavState, {route}) => {
    let authorized = translateAuthorizedPages(
      Features.getPermittedPages({pages: pages()}) as Page[]
    );
    const hasDashboard  = authorized.find(({id}: Page) => id === DASHBOARD.id);
    if (!hasDashboard) {
      authorized = [DASHBOARD, ...authorized] as Page[];
    }
    draft.pages         = authorized as Page[];
    draft.defaultPage   = getDefaultPage(route, isRoute(Route.LOGIN), draft.pages);
    draft.selectedIndex = getIndex(draft.pages, draft.defaultPage);
  },
  [PAGE_SECURITY_INITIALIZED] : (draft: NavState) => {
    draft.pageSecurityInitialized = true;
  },
  [TOGGLE_SECONDARY_NAV] : (draft: NavState, {show, index}) => {
    draft.secondaryNav.show = show;
    draft.selectedIndex = index;
  },
  [SET_SIDE_SHEET_STATUS] : (draft: NavState, page) => {
    draft.sideSheet = {...draft.sideSheet, ...page.data};
  }
};

// defines all reducers for actions of interest to this reducer ...
export default produce((draft: NavState = initialState, action) => {
  reducers?.[action.type]?.(draft, action);
  return draft;
});
